import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Header } from "@/components/Header";
import { TipItem } from "@/components/TipItem";
import { useT, useDocumentTitleKey, useLocale } from "@/i18n/locale-context";

export const Route = createFileRoute("/progress")({
  head: () => ({ meta: [{ title: "Ma progression — Glow Up Coach" }] }),
  component: ProgressPage,
});

type Metric = { label: string; score: number; delta: string };
type Tip = { title: string; detail: string; category?: string; horizon?: string; frequency?: string; disclaimer?: string };
type Analysis = {
  id: string;
  created_at: string;
  goal: string;
  summary: string | null;
  score: number | null;
  metrics: Metric[] | null;
  tips: Tip[] | null;
};

function isoWeek(d: Date): { year: number; week: number; key: string } {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const week = Math.ceil((((date.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return { year: date.getUTCFullYear(), week, key: `${date.getUTCFullYear()}-W${String(week).padStart(2, "0")}` };
}

function ProgressPage() {
  useDocumentTitleKey("progress.head.title");
  const t = useT();
  const { locale } = useLocale();
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<Analysis[]>([]);

  useEffect(() => {
    (async () => {
      const { data: session } = await supabase.auth.getSession();
      if (!session.session) { nav({ to: "/login" }); return; }
      const { data } = await supabase
        .from("analyses")
        .select("id, created_at, goal, summary, score, metrics, tips")
        .order("created_at", { ascending: true })
        .limit(200);
      setRows((data ?? []) as unknown as Analysis[]);
      setLoading(false);
    })();
  }, [nav]);

  // Group by ISO week, keep latest analysis of each week as representative.
  const weeks = (() => {
    const map = new Map<string, { key: string; label: string; date: Date; items: Analysis[] }>();
    for (const a of rows) {
      const d = new Date(a.created_at);
      const { key } = isoWeek(d);
      const weekPrefix = locale === "fr" ? "S" : locale === "nl" ? "W" : "W";
      const label = `${weekPrefix}${key.split("-W")[1]} · ${d.toLocaleDateString(locale, { day: "2-digit", month: "short" })}`;
      const cur = map.get(key);
      if (cur) cur.items.push(a);
      else map.set(key, { key, label, date: d, items: [a] });
    }
    return Array.from(map.values()).sort((a, b) => a.date.getTime() - b.date.getTime());
  })();

  const latest = rows.length ? rows[rows.length - 1] : null;
  const first = rows.length ? rows[0] : null;
  const scoreDelta = latest && first && latest.score != null && first.score != null
    ? latest.score - first.score
    : null;
  const tips = (latest?.tips ?? []).slice(0, 5);

  return (
    <div className="min-h-screen bg-background font-sans text-foreground" style={{ fontFamily: "var(--font-sans)" }}>
      <Header />
      <section className="max-w-5xl mx-auto px-6 py-16">
        <div className="mb-10">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-accent">{t("progress.kicker")}</span>
          <h1 className="font-display font-bold text-4xl md:text-5xl mt-2 mb-3 tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
            {t("progress.title")}
          </h1>
          <p className="text-muted-foreground">{t("progress.subtitle")}</p>
        </div>

        {loading ? (
          <div className="text-sm text-muted-foreground">{t("progress.loading")}</div>
        ) : rows.length === 0 ? (
          <div className="p-8 rounded-3xl border border-foreground/10 bg-muted text-center">
            <p className="font-semibold mb-2">{t("progress.empty.title")}</p>
            <p className="text-sm text-muted-foreground mb-6">{t("progress.empty.desc")}</p>
            <Link to="/app" className="bg-foreground text-background px-5 py-2.5 rounded-full text-sm font-bold hover:opacity-90">
              {t("progress.empty.cta")}
            </Link>
          </div>
        ) : (
          <>
            {/* Overall progression */}
            <div className="grid sm:grid-cols-3 gap-4 mb-10">
              <div className="p-5 rounded-2xl border border-foreground/10 bg-muted">
                <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1">{t("progress.stat.weeks")}</p>
                <p className="font-display text-3xl font-bold" style={{ fontFamily: "var(--font-display)" }}>{weeks.length}</p>
              </div>
              <div className="p-5 rounded-2xl border border-foreground/10 bg-muted">
                <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1">{t("progress.stat.analyses")}</p>
                <p className="font-display text-3xl font-bold" style={{ fontFamily: "var(--font-display)" }}>{rows.length}</p>
              </div>
              <div className="p-5 rounded-2xl border border-accent/30 bg-accent/5">
                <p className="text-[11px] font-bold uppercase tracking-wider text-accent mb-1">{t("progress.stat.delta")}</p>
                <p className="font-display text-3xl font-bold" style={{ fontFamily: "var(--font-display)" }}>
                  {scoreDelta == null ? "—" : `${scoreDelta > 0 ? "+" : ""}${scoreDelta}`}
                </p>
              </div>
            </div>

            {/* Week timeline */}
            <h2 className="font-display font-bold text-2xl mb-4" style={{ fontFamily: "var(--font-display)" }}>
              {t("progress.timeline.title")}
            </h2>
            <ol className="relative border-l border-foreground/10 ml-3 mb-12">
              {weeks.map((w, idx) => {
                const rep = w.items[w.items.length - 1];
                const prev = idx > 0 ? weeks[idx - 1].items[weeks[idx - 1].items.length - 1] : null;
                const d = rep.score != null && prev?.score != null ? rep.score - prev.score : null;
                return (
                  <li key={w.key} className="ml-6 mb-6">
                    <span className="absolute -left-1.5 mt-1.5 size-3 rounded-full bg-accent" />
                    <div className="p-5 rounded-2xl border border-foreground/10 bg-background">
                      <div className="flex items-center justify-between gap-3 mb-2">
                        <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{w.label}</p>
                        <div className="flex items-center gap-2">
                          {rep.score != null && (
                            <span className="text-xs font-bold text-foreground">{rep.score}/100</span>
                          )}
                          {d != null && (
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${d > 0 ? "bg-accent/10 text-accent" : d < 0 ? "bg-foreground/5 text-muted-foreground" : "bg-muted text-muted-foreground"}`}>
                              {d > 0 ? "+" : ""}{d}
                            </span>
                          )}
                        </div>
                      </div>
                      {rep.summary && (
                        <p className="text-sm text-foreground/80 mb-3">{rep.summary}</p>
                      )}
                      {rep.metrics && rep.metrics.length > 0 && (
                        <div className="space-y-2 mt-3">
                          {rep.metrics.slice(0, 3).map((m) => (
                            <div key={m.label}>
                              <div className="flex justify-between text-[11px] font-semibold mb-1">
                                <span className="text-muted-foreground">{m.label}</span>
                                <span className="text-accent">{m.delta}</span>
                              </div>
                              <div className="h-1.5 w-full bg-foreground/5 rounded-full overflow-hidden">
                                <div className="h-full bg-accent" style={{ width: `${Math.max(0, Math.min(100, m.score))}%` }} />
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>

            {/* 3 concrete tips */}
            <div className="bg-primary text-primary-foreground rounded-3xl p-8">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-accent">{t("progress.tips.kicker")}</span>
              <h2 className="font-display font-bold text-3xl mt-2 mb-6" style={{ fontFamily: "var(--font-display)" }}>
                {t("progress.tips.title")}
              </h2>
              {tips.length === 0 ? (
                <p className="text-sm text-primary-foreground/70">{t("progress.tips.empty")}</p>
              ) : (
                <ul className="space-y-5">
                  {tips.map((tip, i) => (
                    <TipItem
                      key={i}
                      index={i}
                      tip={tip}
                      numberClassName="text-accent font-display text-2xl font-bold leading-none"
                      detailClassName="text-primary-foreground/70 text-xs leading-relaxed"
                      badgeClassName="text-[10px] uppercase tracking-wider bg-primary-foreground/10 text-primary-foreground/80 px-2 py-0.5 rounded-full"
                      disclaimerClassName="text-[11px] text-accent/90 mt-2 italic"
                    />
                  ))}
                </ul>
              )}
            </div>

            <div className="mt-10 text-center">
              <Link to="/app" className="text-sm text-muted-foreground hover:text-foreground">
                {t("progress.back_to_app")}
              </Link>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
